import type { ActionFailure } from "@/server/action-error";
import { registerApiFetch } from "@/lib/api-fetch";

/**
 * Worker API 호출기.
 *
 * **서버 액션과 같은 규약을 지킨다**: 실패를 던지지 않고 `{__actionError}` 봉투로
 * 돌려준다. 기존 컴포넌트들이 `run(someAction(...))`으로 부르고 있고
 * (src/lib/action-run.ts), run()이 그 봉투를 Error로 되살린다. 덕분에 컴포넌트를
 * 한 줄도 고치지 않고 API로 갈아끼울 수 있다.
 *
 * 토큰은 Clerk에서 요청 시점에 가져온다 — 모듈 로드 시점에 붙잡아 두면 만료된
 * 토큰을 계속 쓰게 된다.
 */

let getToken: (() => Promise<string | null>) | null = null;

/** 앱 부팅 시 Clerk의 getToken을 심는다(main.tsx). */
export function setTokenProvider(fn: () => Promise<string | null>): void {
  getToken = fn;
}

/** 개발에서는 Vite 프록시가 /api를 Worker로 넘긴다. 배포에서는 같은 오리진이다. */
const BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  /**
   * 요청이 **서버에 닿지도 못했는가**(네트워크 없음). 서버가 준 4xx·5xx와
   * 구분해야 한다 — 오프라인 지출 대기열이 이 구분으로 동작한다
   * (src/lib/offline-expense-flush.ts의 isNetworkError).
   */
  readonly offline: boolean;

  constructor(
    readonly status: number,
    message: string,
    offline = false
  ) {
    super(message);
    this.offline = offline;
  }
}

type Options = { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal };

/**
 * 끊겼다가 **다시 연결됐다**는 신호.
 *
 * `online` 이벤트를 믿을 수 없어서 둔다 — 기내 와이파이나 캡티브 포털에서는
 * 브라우저가 계속 "온라인"이라고 생각하므로 그 이벤트가 아예 안 온다(실측).
 * 요청 하나가 성공하는 것이 연결됐다는 가장 확실한 증거다. 오프라인 지출
 * 대기열이 이 신호를 듣고 밀린 것을 올린다.
 */
export const RECONNECTED_EVENT = "gotravel:reconnected";
let sawOffline = false;

/**
 * JSON을 거치며 문자열이 된 날짜를 `Date`로 되살린다.
 *
 * 서버 함수들은 `createdAt: Date`를 계약으로 내놓는데(sql-exec의 asDate가 그걸
 * 보장한다), `JSON.stringify`를 지나면 ISO 문자열이 된다. 그대로 두면
 * `createdAt.getTime()`을 부르는 곳들이 조용히 깨진다 —
 * `src/lib/album.ts`·`expense-sort.ts`·`pot-balance.ts`가 실제로 그렇게 쓴다.
 *
 * 이름으로 판단한다. 스키마의 timestamp 컬럼이 이 둘뿐이라(createdAt 8곳,
 * takenAt 3곳) 규칙이 단순하고, 값이 ISO 모양일 때만 바꾸므로 오탐이 없다.
 * **새 timestamp 컬럼을 만들면 여기에 이름을 추가해야 한다.**
 */
const DATE_FIELDS = new Set(["createdAt", "takenAt"]);
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

function reviveDates(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(reviveDates);
  if (value === null || typeof value !== "object") return value;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = DATE_FIELDS.has(k) && typeof v === "string" && ISO.test(v) ? new Date(v) : reviveDates(v);
  }
  return out;
}

async function request<T>(path: string, opts: Options = {}): Promise<T> {
  // 토큰을 못 얻어도 요청은 보낸다. **오프라인에서 중요하다** — Clerk의
  // getToken은 갱신이 필요한데 네트워크가 없으면 던지는데, 여기서 같이 던지면
  // 서비스 워커가 들고 있는 캐시된 응답까지 못 쓰게 된다. 온라인이면 그냥 401이다.
  const token = getToken ? await getToken().catch(() => null) : null;
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method: opts.method ?? (opts.body !== undefined || opts.form ? "POST" : "GET"),
      headers,
      body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
      signal: opts.signal,
    });
  } catch (err) {
    // 요청을 **보내지도 못했다**(네트워크 없음). 일반 오류와 섞으면 안 된다 —
    // 지출 대기열은 "네트워크 실패일 때만" 큐에 넣는다. 중단은 그대로 던진다.
    if ((err as { name?: string })?.name === "AbortError") throw err;
    sawOffline = true;
    throw new ApiError(0, "오프라인이라 지금은 저장할 수 없어요", true);
  }

  // 서비스 워커가 오프라인에 대신 만들어 준 응답은 **연결됐다는 증거가 아니다.**
  // 그걸 성공으로 세면 오프라인인데도 "돌아왔다"고 판단한다(실측: 새로고침 반복).
  if (sawOffline && !res.headers.get("x-offline-fallback")) {
    sawOffline = false;
    window.dispatchEvent(new Event(RECONNECTED_EVENT));
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok) {
    const message =
      (parsed as { error?: string } | null)?.error ?? "요청을 처리하지 못했습니다";
    throw new ApiError(res.status, message);
  }
  return reviveDates(parsed) as T;
}

/** 조회용. 실패하면 던진다(화면이 오류 경계로 처리한다). */
export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T,>(path: string, form: FormData) => request<T>(path, { method: "POST", form }),
};

/**
 * 변경용. **서버 액션의 반환 규약을 그대로 흉내 낸다** — 실패를 던지지 않고
 * `{__actionError: 메시지}`를 돌려준다. 그래야 `run()`이 기존처럼 동작한다.
 *
 * 던지는 게 아니라 돌려주는 이유가 중요하다: 컴포넌트 29개가 이미
 * `try { await run(...) } catch (err) { toast(err.message) }` 모양으로 짜여 있고,
 * 그 문구가 서버가 준 한국어 메시지다.
 */
export async function action<T>(fn: () => Promise<T>): Promise<T | ActionFailure> {
  try {
    return await fn();
  } catch (err) {
    // **오프라인은 삼키지 않는다.** `{__actionError}`로 바꿔 버리면 호출부가
    // 평범한 Error로 받게 되고, 지출 대기열이 "네트워크 실패"를 못 알아본다.
    if (err instanceof ApiError && err.offline) throw err;
    return {
      __actionError: err instanceof ApiError ? err.message : "요청을 처리하지 못했습니다",
    };
  }
}

/**
 * 값을 돌려주지 않는 변경 액션용.
 *
 * 원본 서버 액션 대부분이 `Promise<void | ActionFailure>`다. 응답 본문을 그대로
 * 흘려보내면 타입이 `unknown`이 되어 호출부와 어긋난다 — 실제로
 * `offline-expense-queue-section`이 `Promise<void>`를 요구해 걸렸다.
 */
export async function mutate(fn: () => Promise<unknown>): Promise<void | ActionFailure> {
  return action(async () => {
    await fn();
  });
}

/**
 * 공유 컴포넌트(사진·영수증·GPX 업로드)가 쓰는 fetch — **원본 `Response`를 돌려준다.**
 *
 * `request()`는 JSON을 풀어 `ApiError`로 던지는데, 이 컴포넌트들은 Next와 공유하는
 * 코드라 `res.ok`·`res.json()`을 직접 본다. 그래서 그 모양 그대로 두고 **인증만**
 * 얹는다. 맨 fetch로는 Authorization이 없어 전부 401이었다(src/lib/api-fetch.ts).
 *
 * 토큰 취득은 `request()`와 같다 — 못 얻어도 요청은 보낸다(오프라인에서 던지면
 * 서비스 워커의 캐시 응답까지 못 쓴다). `content-type`은 **건드리지 않는다**:
 * FormData면 브라우저가 경계(boundary)까지 넣어 주는데 여기서 덮으면 업로드가 깨진다.
 *
 * 네트워크 오류는 그대로 던진다(컴포넌트가 try/catch로 "업로드에 실패했습니다"를 낸다).
 */
async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken ? await getToken().catch(() => null) : null;
  const headers = new Headers(init.headers);
  if (token && !headers.has("authorization")) headers.set("authorization", `Bearer ${token}`);
  return fetch(BASE + path, { ...init, headers });
}

registerApiFetch(authedFetch);
